import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Phone, Globe, Share2 } from "lucide-react";

export default function PwaWallet() {
  const { hash } = useParams<{ hash: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hash) return;
    api.get(`/api/v1/public/by-pwa/${hash}`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [hash]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  if (!data) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 gap-4">
      <h1 className="text-xl font-bold">Card Not Found</h1>
    </div>
  );

  const { card } = data;
  const name = card.name_en || card.name_tc || "Unknown";
  const title = card.title_en || card.title_tc || "";
  const company = card.company_en || card.company_tc || "";

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: `${name} — card.buzz`,
        url: `${window.location.origin}/u/${card.personal_id}`,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 safe-area-inset">
      <div className="max-w-md mx-auto px-4 py-6">
        {/* Compact card view for PWA */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shrink-0">
              {card.profile_photo_url ? (
                <img src={card.profile_photo_url} alt={name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-xl font-bold">{name.charAt(0)}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 truncate">{name}</h1>
              {title && <p className="text-sm text-primary font-medium truncate">{title}</p>}
              {company && <p className="text-xs text-gray-500 truncate">{company}</p>}
            </div>
            <button onClick={handleShare} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Share2 className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2">
            {card.email && (
              <a href={`mailto:${card.email}`} className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors">
                <Mail className="w-5 h-5 text-primary" />
                <span className="text-[10px] text-gray-500">Email</span>
              </a>
            )}
            {card.phone && (
              <a href={`tel:${card.phone}`} className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors">
                <Phone className="w-5 h-5 text-primary" />
                <span className="text-[10px] text-gray-500">Call</span>
              </a>
            )}
            {card.website && (
              <a href={card.website} target="_blank" rel="noopener" className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors">
                <Globe className="w-5 h-5 text-primary" />
                <span className="text-[10px] text-gray-500">Website</span>
              </a>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          <a href={`/u/${card.personal_id}`} className="text-primary hover:underline">View full card</a>
        </p>
      </div>
    </div>
  );
}
